import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import {
  type DocumentInstanceRead,
  type HipaaAuthorizationSendSchema,
  useMattersHipaaAuthorizationRefreshCreateMutation,
  useMattersHipaaAuthorizationRemindCreateMutation,
  useMattersHipaaAuthorizationSendCreateMutation,
  useMattersHipaaAuthorizationStatusRetrieveQuery,
  useVoidHipaaAuthorizationMutation,
} from '@/services/api/generated/hipaaESignService';

interface UseHipaaESignStatusOptions {
  matterId: string;
  skip?: boolean;
  /** If true, automatically refresh status from Adobe Sign on mount when a request exists */
  refreshOnMount?: boolean;
}

interface UseHipaaESignStatusResult {
  /** Current e-sign document instance status */
  status: DocumentInstanceRead | null;
  /** Whether the status is loading */
  isLoading: boolean;
  /** Whether an e-sign request exists */
  hasRequest: boolean;
  /** Whether the document is awaiting signature */
  isPending: boolean;
  /** Whether the document has been signed */
  isSigned: boolean;
  /** Whether the document has been voided */
  isVoided: boolean;
  /** Send a new e-sign request */
  sendRequest: (
    params: HipaaAuthorizationSendSchema,
  ) => Promise<DocumentInstanceRead>;
  /** Whether sending is in progress */
  isSending: boolean;
  /** Manually refresh status from Adobe Sign */
  refreshStatus: () => Promise<void>;
  /** Whether refresh is in progress */
  isRefreshing: boolean;
  /** Send a reminder to the signer */
  sendReminder: (options: {
    phoneNumber?: string;
    smsMessage?: string;
  }) => Promise<void>;
  /** Whether reminder is being sent */
  isSendingReminder: boolean;
  /** Void/cancel the signature request */
  voidRequest: () => Promise<void>;
  /** Whether voiding is in progress */
  isVoiding: boolean;
  /** Whether the request can be voided (only when pending) */
  canVoid: boolean;
}

export const useHipaaESignStatus = ({
  matterId,
  skip = false,
  refreshOnMount = false,
}: UseHipaaESignStatusOptions): UseHipaaESignStatusResult => {
  const hasRefreshedOnMount = useRef(false);

  const {
    data: status,
    isLoading,
    refetch,
  } = useMattersHipaaAuthorizationStatusRetrieveQuery(
    { matterId },
    { skip: skip || !matterId },
  );

  const [sendMutation, { isLoading: isSending }] =
    useMattersHipaaAuthorizationSendCreateMutation();

  const [refreshMutation, { isLoading: isRefreshingMutation }] =
    useMattersHipaaAuthorizationRefreshCreateMutation();

  const [remindMutation, { isLoading: isSendingReminder }] =
    useMattersHipaaAuthorizationRemindCreateMutation();

  const [voidMutation, { isLoading: isVoiding }] =
    useVoidHipaaAuthorizationMutation();

  const hasRequest = !!status;
  const isPending = status?.status === 'SENT';
  const isSigned =
    status?.status === 'SIGNED' || status?.status === 'COMPLETED';
  const isVoided = status?.status === 'VOIDED';
  const canVoid = isPending; // Can only void when awaiting signature

  // One-time refresh on mount when a pending request exists
  useEffect(() => {
    if (
      refreshOnMount &&
      !hasRefreshedOnMount.current &&
      status?.id &&
      isPending &&
      !isLoading
    ) {
      hasRefreshedOnMount.current = true;
      // Silent refresh - no toast on mount
      refreshMutation({
        matterId,
        hipaaAuthorizationDocumentReferenceSchema: {
          document_instance_id: status.id,
        },
      })
        .unwrap()
        .then(() => refetch())
        .catch(() => {
          // Silently ignore errors on mount refresh
        });
    }
  }, [
    refreshOnMount,
    status?.id,
    isPending,
    isLoading,
    matterId,
    refreshMutation,
    refetch,
  ]);

  const sendRequest = useCallback(
    async (
      params: HipaaAuthorizationSendSchema,
    ): Promise<DocumentInstanceRead> => {
      try {
        const result = await sendMutation({
          matterId,
          hipaaAuthorizationSendSchema: params,
        }).unwrap();
        toast.success('E-signature request sent');
        return result;
      } catch (error) {
        toast.error('Failed to send e-signature request');
        throw error;
      }
    },
    [matterId, sendMutation],
  );

  const refreshStatus = useCallback(async () => {
    if (!status?.id) return;
    try {
      await refreshMutation({
        matterId,
        hipaaAuthorizationDocumentReferenceSchema: {
          document_instance_id: status.id,
        },
      }).unwrap();
      await refetch();
      toast.success('Status refreshed');
    } catch {
      toast.error('Failed to refresh status');
    }
  }, [matterId, status?.id, refreshMutation, refetch]);

  const sendReminder = useCallback(
    async (options: { phoneNumber?: string; smsMessage?: string }) => {
      if (!status?.id) return;
      try {
        const result = await remindMutation({
          matterId,
          hipaaAuthorizationReminderSchema: {
            document_instance_id: status.id,
            send_sms: true,
            sms_message: options.smsMessage,
            sms_recipient_phone: options.phoneNumber || null,
          },
        }).unwrap();
        // The backend may include a `warning` field when the email reminder
        // succeeded but the SMS portion failed (partial success).
        const {warning} = (result as Record<string, unknown>);
        if (typeof warning === 'string') {
          toast.warning(warning);
        } else {
          toast.success('Reminder sent');
        }
      } catch (error) {
        toast.error('Failed to send reminder');
        throw error;
      }
    },
    [matterId, status?.id, remindMutation],
  );

  const voidRequest = useCallback(async () => {
    if (!status?.id) return;
    try {
      await voidMutation({
        matterId,
        hipaaAuthorizationVoidSchema: {
          document_instance_id: status.id,
        },
      }).unwrap();
      await refetch();
      toast.success('HIPAA Authorization request voided');
    } catch (error) {
      toast.error('Failed to void request');
      throw error;
    }
  }, [matterId, status?.id, voidMutation, refetch]);

  return {
    status: status ?? null,
    isLoading,
    hasRequest,
    isPending,
    isSigned,
    isVoided,
    sendRequest,
    isSending,
    refreshStatus,
    isRefreshing: isRefreshingMutation,
    sendReminder,
    isSendingReminder,
    voidRequest,
    isVoiding,
    canVoid,
  };
};
